import {
  CONTACT_VERIFICATION_ROLE,
  CONTACT_VERIFICATION_REMINDER_MS,
  MOCK_OTP_FAIL_CODE,
  clearContactVerificationPromptSession,
  getContactVerificationPromptMessage,
  getContactVerificationState,
  markContactVerificationPromptShown,
  markContactVerified,
  needsContactVerification,
  resetContactVerification,
  shouldShowContactVerificationPrompt,
  submitContactOtp,
  syncContactVerificationFromProfile,
} from "./contactVerification";
import { formatResendLockDuration, parseLockedUntilMs } from "./forgotPasswordResendLock";

const splitFullName = (fullName) => {
  const raw = String(fullName || "").trim();
  if (!raw) {
    return { firstName: "", lastName: "" };
  }

  if (raw.includes(",")) {
    const [first, ...rest] = raw
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    return {
      firstName: first || "",
      lastName: rest.join(" ") || "",
    };
  }

  const parts = raw.split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return { firstName: "", lastName: "" };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

export const getProfileInitials = (firstName, lastName) => {
  const first = (firstName || "").charAt(0);
  const last = (lastName || "").charAt(0);
  const initials = `${first}${last}`.toUpperCase();
  return initials || "FM";
};

export const formatProfileMobile = (mobile) => {
  const digits = String(mobile || "").replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return mobile || "";
};

/** Formats edit-mobile input as xxx-yyy-zzzz (max 10 digits). */
export const formatEditMobileInput = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 10);

  if (digits.length <= 3) {
    return digits;
  }
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const syncFamilyProfileNames = () => {
  const fullName = localStorage.getItem("familyMemberName") || "";
  return splitFullName(fullName);
};

export const mapProfileResetPasswordFields = (data = {}) => {
  const resetBlockedUntilMs = parseLockedUntilMs(data.resetBlockedUntil);
  return {
    enableResetPassword: Boolean(data.enableResetPassword),
    resetBlockedUntilMs,
  };
};

export const isProfileResetPasswordLocked = (profile = {}) => {
  const blockedUntilMs = profile.resetBlockedUntilMs;
  return Boolean(blockedUntilMs && blockedUntilMs > Date.now());
};

export const canUseProfileResetPassword = (profile = {}) => Boolean(profile.enableResetPassword) && !isProfileResetPasswordLocked(profile);

export const getProfileResetPasswordLockRemainingSeconds = (profile = {}) => {
  const blockedUntilMs = profile.resetBlockedUntilMs;
  if (!blockedUntilMs || blockedUntilMs <= Date.now()) {
    return 0;
  }
  return Math.max(0, Math.ceil((blockedUntilMs - Date.now()) / 1000));
};

export const formatProfileResetPasswordLockMessage = (remainingSeconds) => {
  const seconds = Math.max(0, Math.floor(Number(remainingSeconds) || 0));
  if (!seconds) {
    return "You have used the maximum number of password reset attempts. Please try again later.";
  }

  const days = Math.ceil(seconds / 86400);
  if (days >= 1) {
    return `You have used the maximum number of password reset attempts. Try after ${days} ${days === 1 ? "day" : "days"}.`;
  }

  return `You have used the maximum number of password reset attempts. Try in ${formatResendLockDuration(seconds)}.`;
};

export const formatProfileAddress = (address = {}) => {
  const lines = [address.address1, address.address2, [address.city, address.state, address.zipcode].filter(Boolean).join(", ")].filter(Boolean);
  return lines.join("\n");
};

export const mapApiProfileToState = (data = {}) => {
  const parsedName = splitFullName(data.fullName);
  const address = data.address || {};

  return {
    fullName: String(data.fullName || "").trim(),
    firstName: parsedName.firstName,
    lastName: parsedName.lastName,
    email: String(data.emailId || "").trim(),
    mobile: String(data.mobileNumber || "").trim(),
    address1: String(address.address1 || "").trim(),
    address2: String(address.address2 || "").trim(),
    city: String(address.city || "").trim(),
    state: String(address.state || "").trim(),
    zipcode: String(address.zipcode || "").trim(),
    address: formatProfileAddress(address),
    emailVerified: Boolean(data.isEmailVerified),
    mobileVerified: Boolean(data.isMobileVerified),
    hasEmail: Boolean(data.emailId),
    hasMobile: Boolean(data.mobileNumber),
    consentAccepted: data.consentAccepted === true,
    consentAcceptedAt: String(data.consentAcceptedAt || "").trim(),
    ...mapProfileResetPasswordFields(data),
  };
};

export const syncFamilyVerificationFromProfile = (profile) => syncContactVerificationFromProfile(CONTACT_VERIFICATION_ROLE.FAMILY, profile);

export const getFamilyAccountSettings = () => {
  const fullName = localStorage.getItem("familyMemberName") || "";
  const parsedName = splitFullName(fullName);
  const { emailVerified, mobileVerified } = getContactVerificationState(CONTACT_VERIFICATION_ROLE.FAMILY);

  return {
    fullName,
    firstName: parsedName.firstName,
    lastName: parsedName.lastName,
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
    address: "",
    email: "",
    mobile: "",
    emailVerified,
    mobileVerified,
    hasEmail: false,
    hasMobile: false,
    enableResetPassword: false,
    resetBlockedUntilMs: null,
  };
};

export const resetFamilyContactVerification = (target) => resetContactVerification(CONTACT_VERIFICATION_ROLE.FAMILY, target);

export const updateFamilyContact = (target) => {
  resetFamilyContactVerification(target);
};

export const needsFamilyContactVerification = () => needsContactVerification(CONTACT_VERIFICATION_ROLE.FAMILY);

export const getFamilyVerificationPromptMessage = () => getContactVerificationPromptMessage(CONTACT_VERIFICATION_ROLE.FAMILY);

export const FAMILY_VERIFICATION_REMINDER_MS = CONTACT_VERIFICATION_REMINDER_MS;

export const clearFamilyVerificationPromptSession = () => clearContactVerificationPromptSession();

export const markFamilyVerificationPromptShown = () => markContactVerificationPromptShown(CONTACT_VERIFICATION_ROLE.FAMILY);

export const shouldShowFamilyVerificationPrompt = () => shouldShowContactVerificationPrompt(CONTACT_VERIFICATION_ROLE.FAMILY);

export const maskEmail = (email) => {
  if (!email) {
    return "No email on file";
  }
  const [local, domain] = email.split("@");
  if (!domain) {
    return email;
  }
  const visible = local.length <= 2 ? local : `${local.slice(0, 2)}***`;
  return `${visible}@${domain}`;
};

export { MOCK_OTP_FAIL_CODE };

export const getOtpSentMessage = (target) => {
  if (target === "email") {
    return "OTP has been sent to the registered email.";
  }
  return "OTP has been sent to the registered mobile number.";
};

/** Mock OTP verify: any 4-digit code succeeds except 0000. */
export const submitFamilyContactOtp = (target, otp) => submitContactOtp(CONTACT_VERIFICATION_ROLE.FAMILY, target, otp);

export const markFamilyContactVerified = (target) => markContactVerified(CONTACT_VERIFICATION_ROLE.FAMILY, target);

export const maskMobile = (mobile) => {
  if (!mobile) {
    return "No mobile number on file";
  }
  const digits = mobile.replace(/\D/g, "");
  if (digits.length < 4) {
    return mobile;
  }
  return `***-***-${digits.slice(-4)}`;
};
